import { Injectable } from '@angular/core';

export interface SvgValidationResult {
  isValid: boolean;
  sanitizedContent: string;
  errors: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SvgValidatorService {
  // Whitelist of allowed SVG elements
  private readonly allowedElements = new Set([
    'svg',
    'path',
    'circle',
    'rect',
    'line',
    'polyline',
    'polygon',
    'g',
    'defs',
    'clipPath',
    'mask',
    'pattern',
    'linearGradient',
    'radialGradient',
    'stop',
    'text',
    'tspan',
    'switch',
    'foreignObject',
  ]);

  // Whitelist of allowed SVG attributes
  private readonly allowedAttributes = new Set([
    'viewBox',
    'width',
    'height',
    'fill',
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-dasharray',
    'stroke-dashoffset',
    'd',
    'cx',
    'cy',
    'r',
    'x',
    'y',
    'x1',
    'y1',
    'x2',
    'y2',
    'points',
    'rx',
    'ry',
    'transform',
    'opacity',
    'fill-opacity',
    'stroke-opacity',
    'fill-rule',
    'clip-path',
    'mask',
    'id',
    'class',
    'style',
    'role',
    'aria-label',
    'aria-hidden',
    'xmlns',
    'xmlns:xlink',
    'xlink:href',
    'clipPath',
    'maskUnits',
    'maskType',
    'userSpaceOnUse',
    'luminance',
    'url',
    'defs',
    'clipPath',
    'rect',
    'fill-rule',
    'clip-rule',
    'evenodd',
  ]);

  // Dangerous patterns that should be blocked
  private readonly dangerousPatterns = [
    /javascript:/gi,
    /data:text\/html/gi,
    /vbscript:/gi,
    /onload/gi,
    /onerror/gi,
    /onclick/gi,
    /onmouseover/gi,
    /onfocus/gi,
    /onblur/gi,
    /<script/gi,
    /<\/script/gi,
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
    /<link/gi,
    /<meta/gi,
    /<style/gi,
    /expression\s*\(/gi,
    /url\s*\(\s*javascript:/gi, // Only block javascript URLs, not all URLs
  ];

  /**
   * Validates and sanitizes SVG content
   */
  validateAndSanitize(svgContent: string): SvgValidationResult {
    const errors: string[] = [];

    try {
      // Basic validation
      if (!svgContent || typeof svgContent !== 'string') {
        return {
          isValid: false,
          sanitizedContent: '',
          errors: ['Invalid SVG content: must be a non-empty string'],
        };
      }

      // Check for dangerous patterns
      const hasDangerousPatterns = this.dangerousPatterns.some((pattern) =>
        pattern.test(svgContent),
      );

      if (hasDangerousPatterns) {
        errors.push('SVG contains potentially dangerous content');
        return {
          isValid: false,
          sanitizedContent: '',
          errors,
        };
      }

      // Parse and validate SVG structure
      const sanitizedContent = this.sanitizeSvgContent(svgContent, errors);

      return {
        isValid: errors.length === 0,
        sanitizedContent,
        errors,
      };
    } catch (error) {
      return {
        isValid: false,
        sanitizedContent: '',
        errors: [
          `SVG validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ],
      };
    }
  }

  /**
   * Sanitizes SVG content by removing dangerous elements and attributes
   */
  private sanitizeSvgContent(svgContent: string, errors: string[]): string {
    try {
      // Create a temporary DOM element to parse the SVG
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');

      // Check for parsing errors
      const parseError = doc.querySelector('parsererror');
      if (parseError) {
        errors.push('Invalid SVG format: parsing failed');
        return '';
      }

      const svgElement = doc.documentElement;
      if (svgElement.tagName.toLowerCase() !== 'svg') {
        errors.push('Content must be a valid SVG element');
        return '';
      }

      // Sanitize the SVG element and its children
      this.sanitizeElement(svgElement, errors);

      // Serialize back to string
      return new XMLSerializer().serializeToString(svgElement);
    } catch (error) {
      errors.push(
        `SVG sanitization failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
      return '';
    }
  }

  /**
   * Recursively sanitizes an element and its children
   */
  private sanitizeElement(element: Element, errors: string[]): void {
    const tagName = element.tagName.toLowerCase();

    // Check if element is allowed
    if (!this.allowedElements.has(tagName)) {
      errors.push(`Disallowed element: ${tagName}`);
      element.remove();
      return;
    }

    // Sanitize attributes
    const attributesToRemove: string[] = [];
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      const attrName = attr.name.toLowerCase();

      if (!this.allowedAttributes.has(attrName)) {
        attributesToRemove.push(attr.name);
        errors.push(`Disallowed attribute: ${attr.name}`);
      } else {
        // Additional validation for specific attributes
        if (!this.isValidAttributeValue(attrName, attr.value)) {
          attributesToRemove.push(attr.name);
          errors.push(`Invalid attribute value: ${attr.name}="${attr.value}"`);
        }
      }
    }

    // Remove disallowed attributes
    attributesToRemove.forEach((attrName) => {
      element.removeAttribute(attrName);
    });

    // Sanitize child elements
    const children = Array.from(element.children);
    children.forEach((child) => {
      this.sanitizeElement(child, errors);
    });
  }

  /**
   * Validates attribute values for specific attributes
   */
  private isValidAttributeValue(attrName: string, value: string): boolean {
    // Check for dangerous patterns in attribute values
    if (this.dangerousPatterns.some((pattern) => pattern.test(value))) {
      return false;
    }

    // Specific validations for common attributes
    switch (attrName) {
      case 'viewBox':
        return /^\d+(\.\d+)?\s+\d+(\.\d+)?\s+\d+(\.\d+)?\s+\d+(\.\d+)?$/.test(
          value,
        );

      case 'width':
      case 'height':
        return (
          /^\d+(\.\d+)?(px|%|em|rem|vh|vw)?$/.test(value) || value === '100%'
        );

      case 'fill':
      case 'stroke':
        return /^(none|currentColor|#[0-9a-fA-F]{3,6}|rgb\(\d+,\s*\d+,\s*\d+\)|rgba\(\d+,\s*\d+,\s*\d+,\s*[\d.]+\))$/.test(
          value,
        );

      case 'transform':
        return (
          /^(translate|scale|rotate|skew|matrix)\([^)]*\)$/.test(value) ||
          value === ''
        );

      case 'd':
        // Basic path validation - should start with M or m
        return /^[MmLlHhVvCcSsQqTtAaZz\s\d.,-]+$/.test(value);

      case 'points':
        return /^[\d\s.,-]+$/.test(value);

      case 'style':
        // Basic CSS validation - no javascript or dangerous functions
        return !/javascript:|expression\s*\(|url\s*\(/gi.test(value);

      case 'xmlns':
      case 'xmlns:xlink':
        // Allow standard SVG namespaces
        return /^https?:\/\/www\.w3\.org\//.test(value);

      default:
        return true;
    }
  }

  /**
   * Validates that the SVG is safe for use in the application
   */
  isSvgSafe(svgContent: string): boolean {
    const result = this.validateAndSanitize(svgContent);
    return result.isValid;
  }

  /**
   * Gets a safe, sanitized version of the SVG content
   */
  getSafeSvg(svgContent: string): string {
    const result = this.validateAndSanitize(svgContent);
    return result.sanitizedContent;
  }
}
